import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, ReplaySubject, Subject } from 'rxjs';
import {
    NavigationState,
    NavigationStore,
    ProjectInformationService,
    ReleaseStore,
    SessionService,
    SystemService
} from '@frontend-site/shared/core';
import { AnalysisData, Release } from '@frontend-site/shared/data';
import { HybridMessageSubSubject } from '../entities';

@Injectable({
    providedIn: 'root'
})
export class HybridMessagePayloadService {
    constructor(
        private navigationStore: NavigationStore,
        private readonly releaseStore: ReleaseStore,
        private readonly session: SessionService,
        private projectInformationService: ProjectInformationService,
        private systemService: SystemService
    ) {}

    private scriptDetailsSubject = new Subject<any>();
    public scriptDetails$ = this.scriptDetailsSubject.asObservable();

    private scriptViewActionsSubject = new Subject<any>();
    public scriptViewActions$ = this.scriptViewActionsSubject.asObservable();

    private scriptViewEditEventsSubject = new Subject<any>();
    public scriptViewEditEvents$ = this.scriptViewEditEventsSubject.asObservable();

    private stepViewEvidenceEventsSubject = new Subject<any>();
    public stepViewEvidenceEvents$ = this.stepViewEvidenceEventsSubject.asObservable();

    private isScriptDirtySubject = new Subject<any>();
    public isScriptDirty$ = this.isScriptDirtySubject.asObservable();

    private analysisDataSubject = new BehaviorSubject<AnalysisData | null>(null);
    analysisData$ = this.analysisDataSubject.asObservable();

    private requirementToSetInStoreSubject = new BehaviorSubject<unknown | null>(null);
    public requirementToSetInStore$ = this.requirementToSetInStoreSubject.asObservable();

    private titleSubject$ = new ReplaySubject<string>();
    readonly title$ = this.titleSubject$.asObservable().pipe(distinctUntilChanged());

    private busySubject$ = new BehaviorSubject<boolean>(false);
    readonly busy$ = this.busySubject$.asObservable().pipe(distinctUntilChanged());

    private freezeMainHeaderSubject = new BehaviorSubject<boolean>(false);
    readonly freezeMainHeader$ = this.freezeMainHeaderSubject.asObservable();

    private updateMyItemsSubject$ = new Subject<void>();
    readonly updateMyItems$ = this.updateMyItemsSubject$.asObservable();

    private optimizeCycleSubject$ = new Subject<void>();
    readonly optimizeCycle$ = this.optimizeCycleSubject$.asObservable();

    private evidenceDeletedSubject = new Subject<boolean>();
    readonly evidenceDeleted$ = this.evidenceDeletedSubject.asObservable();

    setAnalysisData(analysisData: AnalysisData): void {
        this.analysisDataSubject.next(analysisData);
    }

    public refreshRequirement(requirement: unknown): void {
        this.requirementToSetInStoreSubject.next(requirement);
    }
    public loadProjectInformation(projectId: number): void {
        this.projectInformationService.loadProjectInformation(projectId);
    }

    public loadSystems(): void {
        this.systemService.loadSystems();
    }

    public loadSession(): void {
        this.session.getSessionInformation();
    }

    setTitle(title: string): void {
        this.titleSubject$.next(title);
    }

    setBusy(isBusy: boolean): void {
        this.busySubject$.next(isBusy);
    }

    updateNavigationStore(navigationIds: NavigationState['navigationIds']): void {
        this.navigationStore.updateNavigationIds(navigationIds);
    }

    updateReleases(release: Release[]): void {
        this.releaseStore.setReleases(release);
    }

    toggleFreezeMainHeader(isFreeze: boolean): void {
        this.freezeMainHeaderSubject.next(isFreeze);
    }

    updateMyItems(): void {
        this.updateMyItemsSubject$.next();
    }

    optimizeCycle(): void {
        this.optimizeCycleSubject$.next();
    }

    handleScriptViewServiceMsg(payload: any, subSubject: HybridMessageSubSubject, uuidToSubject: any = {}, callBackUid?: string): void {
        switch (subSubject) {
            case 'UpdateScript':
                if (callBackUid) {
                    if (uuidToSubject[callBackUid]) {
                        uuidToSubject[callBackUid].next(payload);
                        uuidToSubject[callBackUid].complete();
                        uuidToSubject[callBackUid] = null;
                    }
                }
                this.scriptDetailsSubject.next(payload);
                break;
            case 'ScriptAction':
                if (payload?.action) {
                    this.scriptViewActionsSubject.next(payload);
                }
                break;
            case 'EditScriptEvents':
                if (payload.event) {
                    this.scriptViewEditEventsSubject.next(payload);
                }
                break;
            case 'CbtEvent':
                this.stepViewEvidenceEventsSubject.next(payload);
                break;
            case 'updateDirty':
                this.isScriptDirtySubject.next({ isDirty: payload.isDirty, testId: payload.testId });
                break;
            case 'evidenceDeleted':
                this.evidenceDeletedSubject.next(true);
                break;
        }
    }

    public sessionSet(value: any, key: string): void {
        if (value) sessionStorage.setItem(key, JSON.stringify(value));
    }
    public sessionGet(key: string): any {
        const val = sessionStorage.getItem(key);
        if (val) return JSON.parse(val);
        return null;
    }
    public sessionRemove(key: string): void {
        sessionStorage.removeItem(key);
    }
}
